<template>
  <div class="p-4">
    <div class="body p-4">
      <span class="text-primary h2 d-flex align-items-center">
        <span class="text-gray">Configurações NFSe</span>
      </span>

      <div class="card p-4 mt-4">
        <div v-if="loaderActive" class="d-flex justify-content-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Aguarde...</span>
          </div>
        </div>

        <div v-else-if="!configs.length" class="p-2 text-center">
          <span class="badge rounded-pill bg-secondary px-3">
            Nenhuma configuração NFSe encontrada
          </span>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Empresa</th>
                <th>CNPJ (Config)</th>
                <th>CNPJ (Empresa)</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="config in configs" :key="config.id">
                <td class="text-muted small">{{ config.id }}</td>
                <td>{{ config.company_name }}</td>
                <td>
                  <code>{{ formatCNPJ(config.cnpj_config) }}</code>
                </td>
                <td>
                  <code>{{ formatCNPJ(config.cnpj_company) }}</code>
                </td>
                <td>
                  <span v-if="config.is_active" class="badge bg-success">Ativa</span>
                  <span v-else class="badge bg-warning text-dark">Aguardando</span>
                </td>
                <td class="text-muted small">{{ formatDate(config.created_at) }}</td>
                <td>
                  <button
                    v-if="config.is_active"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="actionLoading === config.id"
                    @click="deactivate(config)"
                  >
                    <span v-if="actionLoading === config.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Desativar
                  </button>
                  <button
                    v-else
                    class="btn btn-sm btn-outline-success"
                    @click="openActivateModal(config)"
                  >
                    Ativar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Activation confirmation modal -->
    <div v-if="confirmModal.show" class="modal-backdrop-custom" @click.self="closeModal">
      <div class="modal-dialog-custom">
        <div class="card p-4" style="max-width: 520px; width: 100%;">
          <h5 class="mb-3">Confirmar ativação</h5>

          <div class="alert alert-warning" role="alert">
            <i class="fa-solid fa-triangle-exclamation me-2"></i>
            <strong>Atenção:</strong> Antes de ativar, compare o CNPJ abaixo com o CNPJ
            do certificado cadastrado na NS para esta empresa. Ativar uma configuração
            com CNPJ incorreto pode causar emissões inválidas.
          </div>

          <div class="mb-3 text-center">
            <p class="mb-1 text-muted small">Empresa</p>
            <p class="fw-bold">{{ confirmModal.config && confirmModal.config.company_name }}</p>
          </div>

          <div class="mb-4">
            <label class="form-label fw-semibold">
              CNPJ da configuração
              <span class="text-muted fw-normal small">(compare com o certificado NS)</span>
            </label>
            <input
              type="text"
              class="form-control form-control-lg text-center fw-bold"
              v-model="confirmModal.cnpjDisplay"
              @input="onCnpjInput"
              placeholder="00.000.000/0000-00"
              maxlength="18"
            />
            <div v-if="confirmModal.cnpjRaw.length > 0 && confirmModal.cnpjRaw.length !== 14" class="form-text text-danger">
              O CNPJ deve ter 14 dígitos.
            </div>
          </div>

          <div class="d-flex gap-2 justify-content-end">
            <button class="btn btn-secondary" @click="closeModal">Cancelar</button>
            <button
              class="btn btn-success"
              :disabled="confirmModal.cnpjRaw.length !== 14 || actionLoading === confirmModal.config?.id"
              @click="confirmActivate"
            >
              <span v-if="actionLoading === confirmModal.config?.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Confirmar ativação
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "admin-nfse-configs",
  data() {
    return {
      loaderActive: false,
      actionLoading: null,
      configs: [],
      confirmModal: {
        show: false,
        config: null,
        cnpjRaw: "",
        cnpjDisplay: "",
      },
    };
  },
  async mounted() {
    await this.loadConfigs();
  },
  methods: {
    async loadConfigs() {
      try {
        this.loaderActive = true;
        const { data } = await api.get("/admin/nfse/configs");
        this.configs = data;
      } catch (e) {
        toast.error(this.getErrorMessage(e, "Erro ao carregar as configurações NFSe."));
      } finally {
        this.loaderActive = false;
      }
    },

    openActivateModal(config) {
      const raw = String(config.cnpj_config || "").replace(/\D/g, "");
      this.confirmModal = {
        show: true,
        config,
        cnpjRaw: raw,
        cnpjDisplay: this.formatCNPJ(raw),
      };
    },

    closeModal() {
      this.confirmModal.show = false;
      this.confirmModal.config = null;
      this.confirmModal.cnpjRaw = "";
      this.confirmModal.cnpjDisplay = "";
    },

    onCnpjInput(e) {
      const raw = e.target.value.replace(/\D/g, "").slice(0, 14);
      this.confirmModal.cnpjRaw = raw;
      this.confirmModal.cnpjDisplay = this.formatCNPJ(raw);
    },

    async confirmActivate() {
      const config = this.confirmModal.config;
      if (!config || this.confirmModal.cnpjRaw.length !== 14) return;

      try {
        this.actionLoading = config.id;
        await api.patch(`/admin/nfse/configs/${config.id}/activate`, {
          cnpj: this.confirmModal.cnpjRaw,
        });
        toast.success("Configuração NFSe ativada com sucesso.");
        this.closeModal();
        await this.loadConfigs();
      } catch (e) {
        toast.error(this.getErrorMessage(e, "Erro ao ativar a configuração NFSe."));
      } finally {
        this.actionLoading = null;
      }
    },

    async deactivate(config) {
      try {
        this.actionLoading = config.id;
        await api.patch(`/admin/nfse/configs/${config.id}/deactivate`);
        toast.success("Configuração NFSe desativada com sucesso.");
        await this.loadConfigs();
      } catch (e) {
        toast.error(this.getErrorMessage(e, "Erro ao desativar a configuração NFSe."));
      } finally {
        this.actionLoading = null;
      }
    },

    formatCNPJ(digits) {
      const d = String(digits || "").replace(/\D/g, "").slice(0, 14);
      if (d.length <= 2) return d;
      if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
      if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
      if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
      return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
    },

    formatDate(iso) {
      if (!iso) return "-";
      return new Date(iso).toLocaleString("pt-BR");
    },

    getErrorMessage(e, fallback) {
      return e?.response?.data?.message || fallback || "Ocorreu um erro inesperado.";
    },
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog-custom {
  display: flex;
  justify-content: center;
  padding: 1rem;
  width: 100%;
}

code {
  font-size: 0.9em;
  color: #495057;
}
</style>
